import { useMemo, useRef } from "react";
import { useIsMobile } from "../Layout/useIsMobile";
import Accordion from "../Accordion";
import AnchorBar from "../AnchorBar";
import Presentation from "../Presentation";
import Slider from "../Slider";

import './PageProjectVisualIdentity.scss';

export const PageProjectVisualIdentity = () => {
  const isMobile = useIsMobile();
  const contextRef = useRef(null);
  const resultsRef = useRef(null);
  const searchRef = useRef(null);
  const conclusionRef = useRef(null);
  const sliderItems = useMemo(() => [
    {
      classSuffix: "blissim-gift",
      image: "/images/Blissim/Gift/presentation-mobile.png",
      subtitle: "REFONTE WEB",
      title: "Blissim - Gift",
      url: "/projet/blissim-gift"
    },
    {
      classSuffix: "blissim-rgpd",
      image: "/images/Blissim/RGPD/presentation-mobile.png",
      subtitle: "DESIGN LÉGAL",
      title: "Blissim - RGPD",
      url: "/projet/blissim-rgpd"
    },
    {
      classSuffix: "o-revoir",
      image: "/images/ORevoir/presentation-mobile.png",
      subtitle: "BUSINESS PLAN",
      title: "ô revoir",
      url: "/projet/o-revoir"
    }
  ], []);

  const anchors = useMemo(() => ([
    { id: "context", ref: contextRef, title: "Contexte" },
    { id: "search", ref: searchRef, title: "Analyse & Recherches" },
    { id: "results", ref: resultsRef, title: "Résultats" },
    { id: "conclusion", ref: conclusionRef, title: "Conclusion" },
  ]), []);

  const problemTitle = "Problème à résoudre";
  const problemText = "Comment traduire visuellement qui je suis : mon style, mes valeurs et mon approche du design, tout en gardant une image sobre, lisible, professionnelle et adaptée aux standards web.";

  return (
    <div className="page-project visual-identity">
      <Presentation
        alt="Identité visuelle icon"
        classSuffix="visual-identity"
        image={isMobile ? "/images/VisualIdentity/presentation-mobile.png" : "/images/VisualIdentity/presentation-desktop.png"}
        title="Identité visuelle"
        subtitle="CHARTE GRAPHIQUE / DA"
      />

      <AnchorBar anchors={anchors} />

      <section className="anchor-section">
        <h2 ref={contextRef} id="discover" className="anchor--title">Contexte</h2>
        <hr className="anchor--separator" />

        <div className="discover-information">
          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title">Objectifs</h3>
            <ul>
              <li>Créer un univers graphique cohérent qui me représente à travers tous mes supports (portfolio, CV, réseaux, prototype). </li>
            </ul>
          </div>

          <div id="tools" className="discover-information-wrapper">
            <h3 className="discover-information--title">Outils</h3>
            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-ai.svg" alt="icon Illustrator" />
                <span className="icon-tool-title">Illustrator</span>
              </div>
            </div>

            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-photoshop.svg" alt="icon Photoshop" />
                <span className="icon-tool-title">Photoshop</span>
              </div>
            </div>

            <div className="discover-information--list">
              <div className="discover-information-list-item">
                <img className="icon-tool" src="/images/icon-figma.svg" alt="icon Figma" />
                <span className="icon-tool-title">Figma</span>
              </div>
            </div>
          </div>

          <div id="target" className="discover-information-wrapper">
            <h3 className="discover-information--title">Durée</h3>
            <p className="discover-information--description">
              ~ 3 semaines
            </p>
          </div>
        </div>

        <div id="problem" className="discover-information-wrapper">
          <Accordion
            accordionName="accordion--discover-information__problem"
            content={problemText}
            label={problemTitle}
          >
            <>
              <h3 className="discover-information--title">{problemTitle}</h3>
              <p className="discover-information--description__full-size" dangerouslySetInnerHTML={{ __html: problemText }} />
            </>
          </Accordion>
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={searchRef} id="define" className="anchor--title">Analyse & Recherches</h2>
        <hr className="anchor--separator" />

        <div>
          <h3 className="define--title">Analyse</h3>

          <div className="define--block__first">
            <div>
              <h4 className="define-block--subtitle">🧑‍💻 Observer</h4>
              <p>
                J’ai commencé par analyser des portfolios de designers (UI, branding, produit) pour identifier
                ce qui fonctionnait : équilibre des couleurs, usage de la typographie, poids des visuels,
                traitement du logo, etc.
              </p>
            </div>

            <div>
              <h4 className="define-block--subtitle">💼 Définir</h4>
              <p>
                Ensuite, j’ai cherché à définir une palette de couleurs et une typographie alignées à mes valeurs :
              </p>
              <ul>
                <li>Sobriété, accessibilité et modernité pour refléter mon approche.</li>
                <li>Un logo minimaliste, basé sur mes initiales, déclinable et impactant.</li>
                <li>Des visuels structurés, qui laissent respirer le contenu, tout en restant reconnaissables et forts visuellement.</li>

              </ul>
            </div>
          </div>
        </div>

        <div className="define--block">
          <h3 className="define--title">Inspiration</h3>

          <img alt="Inspiration" className="image__fullsize" src="/images/VisualIdentity/visual-identity-inspire.png" />
        </div>

        <div className="define--block">
          <h3 className="define--title">Recherche logo type</h3>

          <img alt="Logos types" className="image__fullsize" src="/images/VisualIdentity/visual-identity-logo-search.png" />
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={resultsRef} id="design" className="anchor--title">Résultats</h2>
        <hr className="anchor--separator" />

        <div className="define--block__logotype">
          <h3 className="define--title">Logotype</h3>
          <p className="define--subtitle">L’identité visuelle repose sur un monogramme épuré, reconnaissable et ancré dans mon univers graphique.</p>

          <img alt="Logos types" className="image__fullsize" src="/images/VisualIdentity/visual-identity-logotype.png" />
        </div>

        <div className="define--block__logotype">
          <h3 className="define--title">Palettes de couleurs</h3>
          <p className="define--subtitle">Chaque couleur a été choisie avec soin : le bleu pour sa stabilité et sa force, le rose pour sa douceur et son accessibilité.</p>

          <img alt="Palettes de couleurs" className="image__fullsize" src="/images/VisualIdentity/visual-identity-colors.png" />
        </div>

        <div className="define--block">
          <h3 className="define--title">Déclinaison</h3>

          <img alt="Déclinaison cartes" className="image__fullsize" src="/images/VisualIdentity/visual-identity-cards.png" />
          <img alt="Déclinaison magazine" className="image__fullsize" src="/images/VisualIdentity/visual-identity-magazine.png" />
          <img alt="Déclinaison cartes" className="image__fullsize" src="/images/VisualIdentity/visual-identity-cup.png" />
          <img alt="Déclinaison cartes" className="image__fullsize" src="/images/VisualIdentity/visual-identity-pantone.png" />
          <img alt="Déclinaison cartes" className="image__fullsize" src="/images/VisualIdentity/visual-identity-tabs.png" />
          <img alt="Déclinaison cartes" className="image__fullsize" src="/images/VisualIdentity/visual-identity-website.png" />
        </div>
      </section>

      <section className="anchor-section">
        <h2 ref={conclusionRef} id="realize" className="anchor--title">Conclusion</h2>
        <hr className="anchor--separator" />

        <div className="define--benchmark-card define--benchmark-card__green">
          <div className="define--benchmark-card__text">
            <h4 className="define--benchmark-card__title">Une base solide pour communiquer</h4>

            <p>
              Ce projet d'identité visuelle m’a permis d’ancrer ma pratique dans un univers graphique propre, cohérent
              et affirmé.Au-delà de l’esthétique, cette direction artistique me sert aujourd’hui de cadre de travail,
              facilitant la lisibilité, la cohérence de mes supports et ma reconnaissance professionnelle.
              <br />
              <br />
              👉 Un design pensé comme un prolongement naturel de ma façon de travailler : clair, structuré, réfléchi,
              et humain.
            </p>
          </div>
        </div>
      </section>

      <Slider sliderItems={sliderItems} />
    </div>
  );
};

export default PageProjectVisualIdentity;